import { useEffect, useMemo, useState } from "react";
import { Search, X } from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { useTenant } from "@/contexts/TenantContext";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { ExportMenu } from "@/components/reports/ExportMenu";
import { ShareExportPanel } from "@/components/reports/ShareExportPanel";

const ROTATION_LABEL = {
  high: { label: "Alta rotación", tone: "bg-emerald-100 text-emerald-800" },
  medium: { label: "Media rotación", tone: "bg-blue-100 text-blue-800" },
  low: { label: "Baja rotación", tone: "bg-amber-100 text-amber-800" },
  slow_mover: { label: "Slow mover", tone: "bg-red-100 text-red-800" },
  no_data: { label: "Sin salidas", tone: "bg-slate-100 text-slate-700" },
};

const MOVEMENT_LABEL = {
  purchase: "Compra",
  sale: "Venta",
  adjustment: "Ajuste",
  transfer_in: "Transferencia (+)",
  transfer_out: "Transferencia (−)",
  return: "Devolución",
  initial: "Inicial",
};

function RotationBadge({ cls }) {
  const cfg = ROTATION_LABEL[cls] || ROTATION_LABEL.no_data;
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${cfg.tone}`}>
      {cfg.label}
    </span>
  );
}

export default function Kardex() {
  const { isSuperAdmin, activeTenantId } = useTenant();
  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [selectedPartId, setSelectedPartId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const params = useMemo(() => {
    const p = {};
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (isSuperAdmin && activeTenantId) p.tenant_id = activeTenantId;
    return p;
  }, [range, branchId, isSuperAdmin, activeTenantId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingSummary(true);
      try {
        const { data: d } = await api.get("/inventory/kardex-summary", { params });
        if (!cancelled) setSummary(d);
      } catch (_) {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoadingSummary(false);
      }
    })();
    return () => { cancelled = true; };
  }, [params]);

  const filteredItems = useMemo(() => {
    const items = summary?.items || [];
    if (!search) return items;
    const s = search.toLowerCase();
    return items.filter(
      (it) =>
        (it.sku || "").toLowerCase().includes(s) ||
        (it.name || "").toLowerCase().includes(s) ||
        (it.category || "").toLowerCase().includes(s)
    );
  }, [summary, search]);

  useEffect(() => {
    if (!selectedPartId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDetail(true);
      try {
        const { data: d } = await api.get(`/inventory/kardex/${selectedPartId}`, { params });
        if (!cancelled) setDetail(d);
      } catch (_) {
        if (!cancelled) setDetail(null);
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedPartId, params]);

  return (
    <div className="space-y-6" data-testid="page-kardex">
      <PageHeader
        title="Kardex analítico"
        description="Movimientos por refacción, rotación y stock. Búsqueda por SKU, nombre o categoría."
        actions={
          <div className="flex gap-2 flex-wrap">
            <ExportMenu
              endpoint="/inventory/kardex-summary"
              params={params}
              permission="analytics:export"
              filename="kardex-resumen"
              label="Exportar resumen"
              testId="export-kardex-summary"
            />
            {selectedPartId && (
              <ShareExportPanel
                reportKey="kardex_movements"
                reportName="Kardex analítico"
                endpoint={`/inventory/kardex/${selectedPartId}`}
                params={params}
                exportPermission="analytics:export"
                filename="kardex-detalle"
                filters={{
                  part_sku: detail?.part?.sku,
                  part_name: detail?.part?.name,
                  date_from: range.date_from,
                  date_to: range.date_to,
                }}
                stats={{
                  movements_count: detail?.movements?.length || 0,
                  entries_qty: detail?.totals?.entries_qty,
                  exits_qty: detail?.totals?.exits_qty,
                  current_stock: detail?.part?.stock,
                  row_count: detail?.movements?.length || 0,
                }}
                testIdPrefix="kardex-detail-share"
              />
            )}
          </div>
        }
      />

      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <div className="flex flex-wrap items-end gap-3">
            <BranchFilter value={branchId} onChange={setBranchId} />
            <div className="relative">
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Buscar
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="SKU, nombre o categoría"
                  className="pl-8 h-9 w-64"
                  data-testid="kardex-search"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: list */}
        <Card className={`border-border ${selectedPartId ? "lg:col-span-1" : "lg:col-span-3"}`} data-testid="kardex-list">
          <CardHeader className="border-b border-border">
            <CardTitle className="tracking-tight">Refacciones</CardTitle>
            <CardDescription>
              {loadingSummary ? "Cargando…" : `${filteredItems.length} resultados`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loadingSummary ? (
              <div className="p-6 space-y-2">
                {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8" />)}
              </div>
            ) : filteredItems.length ? (
              <div className="max-h-[560px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Nombre</TableHead>
                      <TableHead className="text-right">Stock</TableHead>
                      <TableHead className="text-right">Salidas</TableHead>
                      <TableHead>Rotación</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((it) => (
                      <TableRow
                        key={it.part_id}
                        className={`cursor-pointer ${selectedPartId === it.part_id ? "bg-slate-50" : ""}`}
                        onClick={() => setSelectedPartId(it.part_id)}
                        data-testid={`kardex-row-${it.part_id}`}
                      >
                        <TableCell className="font-mono text-xs">{it.sku}</TableCell>
                        <TableCell className="text-sm">
                          {it.name}
                          {it.below_min && (
                            <span className="ml-2 text-[10px] text-amber-700 font-medium">
                              ↓ min
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">{it.current_stock}</TableCell>
                        <TableCell className="text-right">{it.exits}</TableCell>
                        <TableCell><RotationBadge cls={it.rotation_class} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="p-6 text-sm text-muted-foreground">Sin refacciones.</div>
            )}
          </CardContent>
        </Card>

        {/* Right: detail */}
        {selectedPartId && (
          <Card className="border-border lg:col-span-2" data-testid="kardex-detail">
            <CardHeader className="border-b border-border flex flex-row items-start justify-between">
              <div>
                <CardTitle className="tracking-tight">
                  {detail?.part?.name || "Detalle"}
                </CardTitle>
                <CardDescription className="font-mono text-xs">
                  {detail?.part?.sku}
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedPartId(null)}
                data-testid="kardex-detail-close"
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              {loadingDetail ? (
                <Skeleton className="h-40" />
              ) : detail ? (
                <>
                  {/* Metrics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Stock actual</div>
                      <div className="text-xl font-semibold">{detail.totals.current_stock_scope}</div>
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Entradas</div>
                      <div className="text-xl font-semibold text-emerald-700">+{detail.totals.entries}</div>
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Salidas</div>
                      <div className="text-xl font-semibold text-red-700">−{detail.totals.exits}</div>
                    </div>
                    <div className="rounded-md border border-border p-3">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Rotación</div>
                      <div className="flex items-center gap-2">
                        <div className="text-xl font-semibold">{detail.totals.rotation_index}</div>
                        <RotationBadge cls={detail.totals.rotation_class} />
                      </div>
                    </div>
                  </div>

                  {/* By month */}
                  {detail.monthly && detail.monthly.length > 0 && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                        Por mes
                      </div>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Mes</TableHead>
                            <TableHead className="text-right">Entradas</TableHead>
                            <TableHead className="text-right">Salidas</TableHead>
                            <TableHead className="text-right">Movs.</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detail.monthly.map((m) => (
                            <TableRow key={m.month}>
                              <TableCell className="font-mono text-xs">{m.month}</TableCell>
                              <TableCell className="text-right text-emerald-700">+{m.entries}</TableCell>
                              <TableCell className="text-right text-red-700">−{m.exits}</TableCell>
                              <TableCell className="text-right">{m.movements}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Movements */}
                  <div>
                    <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">
                      Movimientos del período ({detail.movements.length})
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Fecha</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Sucursal</TableHead>
                            <TableHead className="text-right">Δ</TableHead>
                            <TableHead className="text-right">Stock resultante</TableHead>
                            <TableHead>Referencia</TableHead>
                            <TableHead>Usuario</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {detail.movements.map((m) => (
                            <TableRow key={m.id} data-testid={`kardex-mov-${m.id}`}>
                              <TableCell className="font-mono text-xs">{m.date}</TableCell>
                              <TableCell>
                                <Badge variant="secondary" className="text-[10px]">
                                  {MOVEMENT_LABEL[m.movement_type] || m.movement_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-xs">{m.branch_name || "—"}</TableCell>
                              <TableCell className={`text-right font-medium ${m.quantity_delta > 0 ? "text-emerald-700" : "text-red-700"}`}>
                                {m.quantity_delta > 0 ? "+" : ""}{m.quantity_delta}
                              </TableCell>
                              <TableCell className="text-right">{m.stock_after ?? "—"}</TableCell>
                              <TableCell className="text-xs text-muted-foreground">
                                {m.reference_type || "—"}{m.reference_id ? ` · ${m.reference_id.slice(0, 8)}` : ""}
                              </TableCell>
                              <TableCell className="text-xs">{m.created_by_name || "—"}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>

                  <div className="text-[10px] text-muted-foreground">
                    Rotación heurística: alta ≥ 2.0 · media 0.5–2.0 · baja &lt; 0.5 · slow mover si &gt; 60 días sin salida.
                    Estos umbrales son ajustables (backend).
                  </div>
                </>
              ) : (
                <div className="text-sm text-muted-foreground">Selecciona una refacción.</div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
